import { Component, inject, signal, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ToastService, Toast, ToastType } from '../../../core/services/toast.service';

interface ToastConfig {
  icon: string;
  iconColor: string;
  barColor: string;
  bgClass: string;
}

@Component({
  selector: 'app-toast',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div
      class="fixed top-5 right-5 z-[9999] flex flex-col gap-2.5 pointer-events-none"
      style="max-width: 440px; min-width: 320px">

      @for (toast of toastService.toasts(); track toast.id) {
        <div
          class="pointer-events-auto flex items-start gap-3 rounded-xl px-4 py-3.5 shadow-2xl"
          style="
            background: var(--toast-bg, #1a2535);
            border: 1px solid rgba(255,255,255,0.07);
            animation: toastSlideIn 0.28s cubic-bezier(0.34,1.56,0.64,1) both;
          ">

          <!-- Icon -->
          <div
            class="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
            [style.background]="typeConfig(toast.type).iconBg">
            <svg
              class="w-4 h-4"
              [style.color]="typeConfig(toast.type).iconColor"
              fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round"
                [attr.d]="typeConfig(toast.type).iconPath"/>
            </svg>
          </div>

          <!-- Content -->
          <div class="flex-1 min-w-0 pt-0.5">
            @if (toast.title) {
              <div class="text-sm font-semibold text-white mb-0.5">{{ toast.title }}</div>
            }
            <div class="text-sm leading-relaxed" style="color:rgba(255,255,255,0.78)">
              {{ toast.message }}
            </div>
          </div>

          <!-- Dismiss -->
          <button
            class="text-xs font-semibold flex-shrink-0 mt-0.5 transition-opacity hover:opacity-80"
            [style.color]="typeConfig(toast.type).dismissColor"
            (click)="toastService.dismiss(toast.id)">
            Dismiss
          </button>
        </div>

        <!-- Progress bar -->
        @if (toast.duration > 0) {
          <div
            class="-mt-2 h-0.5 rounded-b-full mx-4 pointer-events-none"
            [style.background]="typeConfig(toast.type).barColor"
            [style.animation]="'toastProgress ' + toast.duration + 'ms linear forwards'">
          </div>
        }
      }
    </div>

    <!-- Global keyframes injected once -->
    <style>
      @keyframes toastSlideIn {
        from { opacity: 0; transform: translateX(32px) scale(0.96); }
        to   { opacity: 1; transform: translateX(0) scale(1); }
      }
      @keyframes toastProgress {
        from { transform: scaleX(1); transform-origin: left; }
        to   { transform: scaleX(0); transform-origin: left; }
      }
      [data-theme="light"] { --toast-bg: #1e293b; }
    </style>
  `,
})
export class ToastComponent {
  readonly toastService = inject(ToastService);

  typeConfig(type: ToastType): {
    iconBg: string; iconColor: string; iconPath: string; barColor: string; dismissColor: string;
  } {
    switch (type) {
      case 'success':
        return {
          iconBg: 'rgba(16,185,129,0.18)',
          iconColor: '#34d399',
          iconPath: 'M5 13l4 4L19 7',
          barColor: '#10b981',
          dismissColor: '#34d399',
        };
      case 'error':
        return {
          iconBg: 'rgba(239,68,68,0.18)',
          iconColor: '#f87171',
          iconPath: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
          barColor: '#ef4444',
          dismissColor: '#f87171',
        };
      case 'warning':
        return {
          iconBg: 'rgba(245,158,11,0.18)',
          iconColor: '#fbbf24',
          iconPath: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008z',
          barColor: '#f59e0b',
          dismissColor: '#fbbf24',
        };
      default: // info
        return {
          iconBg: 'rgba(99,102,241,0.18)',
          iconColor: '#818cf8',
          iconPath: 'M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25z',
          barColor: '#6366f1',
          dismissColor: '#818cf8',
        };
    }
  }
}
